import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Building2, Plus, Sparkles, UserPlus, Zap, FileText, Settings, KeyRound, ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { MODULES } from "@/lib/modules";
import { useAuth } from "@/contexts/AuthContext";

export function CommandPalette({ open, setOpen }) {
  const [query, setQuery] = useState("");
  const [companies, setCompanies] = useState([]);
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "administrador";
  const canManage = ["administrador", "socio", "gerente"].includes(user?.role);

  useEffect(() => {
    if (!query || query.length < 2) {
      setCompanies([]);
      return;
    }
    const t = setTimeout(() => {
      api.get(`/companies?q=${encodeURIComponent(query)}&limit=6`)
        .then((r) => setCompanies(r.data.items || []))
        .catch(() => setCompanies([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const go = (path) => { setOpen(false); nav(path); };

  const visibleModules = MODULES.filter((m) => !m.roles || m.roles.includes(user?.role));

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput
        placeholder="Buscar empresas, CNPJ, certidões, obrigações, ações…"
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList>
        <CommandEmpty>Nada encontrado.</CommandEmpty>

        {companies.length > 0 && (
          <CommandGroup heading="Empresas">
            {companies.map((c) => (
              <CommandItem
                key={c.id}
                onSelect={() => go(`/app/empresas/${c.id}`)}
                data-testid={`command-company-${c.id}`}
              >
                <Building2 className="h-4 w-4 mr-2" />
                <span className="flex-1 truncate">{c.razao_social}</span>
                <span className="text-xs text-muted-foreground font-mono">
                  {c.health_score}/100
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading="Ações rápidas">
          <CommandItem onSelect={() => go("/app/prioridades")} data-testid="cmd-prioridades">
            <Sparkles className="h-4 w-4 mr-2" /> Central de Prioridades
          </CommandItem>
          <CommandItem onSelect={() => go("/app/empresas?new=1")} data-testid="cmd-nova-empresa">
            <Plus className="h-4 w-4 mr-2" /> Nova empresa
          </CommandItem>
          <CommandItem onSelect={() => go("/app/ia")} data-testid="cmd-copiloto">
            <Sparkles className="h-4 w-4 mr-2" /> Perguntar ao Copiloto IA
          </CommandItem>
          {canManage && (
            <CommandItem onSelect={() => go("/app/usuarios")} data-testid="cmd-convidar">
              <UserPlus className="h-4 w-4 mr-2" /> Convidar usuário
            </CommandItem>
          )}
          {canManage && (
            <CommandItem onSelect={() => go("/app/automacoes")} data-testid="cmd-automacao">
              <Zap className="h-4 w-4 mr-2" /> Executar automação
            </CommandItem>
          )}
          <CommandItem onSelect={() => go("/app/relatorios")} data-testid="cmd-relatorio">
            <FileText className="h-4 w-4 mr-2" /> Ver relatórios
          </CommandItem>
        </CommandGroup>

        <CommandGroup heading="Módulos">
          {visibleModules.map((m) => (
            <CommandItem
              key={m.slug}
              onSelect={() => go(m.path)}
              data-testid={`cmd-nav-${m.slug}`}
            >
              <m.icon className="h-4 w-4 mr-2" />
              <span className="flex-1 truncate">{m.label}</span>
              {!m.available && (
                <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded border border-warning/30 text-warning">
                  Em breve
                </span>
              )}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandGroup heading="Configurações">
          <CommandItem onSelect={() => go("/app/configuracoes")} data-testid="cmd-config-hub">
            <Settings className="h-4 w-4 mr-2" /> Escritório & Perfil
          </CommandItem>
          {canManage && (
            <CommandItem onSelect={() => go("/app/auditoria")} data-testid="cmd-config-audit">
              <ShieldCheck className="h-4 w-4 mr-2" /> Auditoria
            </CommandItem>
          )}
          {isAdmin && (
            <CommandItem onSelect={() => go("/app/api-keys")} data-testid="cmd-config-apikeys">
              <KeyRound className="h-4 w-4 mr-2" /> API Keys
            </CommandItem>
          )}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
